import { useContext, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Group,
  Breadcrumbs,
  Divider,
  Box,
  Space,
  Anchor,
  Stack,
  Button,
  Loader,
  ActionIcon,
  LoadingOverlay,
  Tabs,
} from '@mantine/core';
import { Container } from 'reactstrap';
import { DndProvider } from 'react-dnd-latest';
import { HTML5Backend } from 'react-dnd-html5-backend-latest';
import { IconEye, IconPilcrow, IconSettings, IconSignature } from '@tabler/icons';
import { QuestionnaireContext } from '../../Service/QuestionnaireContextV2';
import LinkText from '../../Atoms/LinkText';
import SkeletonItems, { QuestionBundleSkeleton } from '../../Atoms/SkeletonItems';
import QuestionBundle from '../../Molecules/Settings/QuestionnaireTemplate/QuestionBundle';
import { EventRichInput, RichInput } from '../../Organismns/tiptap/TiptapEditorInput';
import DocumentBubbleMenu from '../../Organismns/tiptap/toolbars/BubbleMenu';
import { TiptapS } from '../../Service/TiptapS';
import QuestionnaireAlert from '../../Atoms/QuestionnaireAlert';
import { WindowContext } from '../../Service/Context/WindowContext';
import { TestS } from '../../TestS';
import { PlaceholderS } from '../../Service/PlaceholderS';
import { useEventScope } from '../../Service/hooks/useEventScope';
import BookitupTextInput from '../../Atoms/inputs/BookitupTextInput';
import DocumentLanguagePicker from '../../Atoms/inputs/DocumentLanguagePicker';
import { FooterGap } from '../Navigation/phone/InjectableFooter';
import QuestionnaireActions from './QuestionnaireActions';
import QuestionnaireSignatureSection from './QuestionnaireSignatureSection';

const QuestionnaireEditor = () => {
  const { loading, insertingBundle, questionnaire, patchQuestionnaire, bundleHandlers, questionHandlers } =
    useContext(QuestionnaireContext);
  const navigate = useNavigate();
  const { isEventScope } = useEventScope();
  const { sm } = useContext(WindowContext);
  const [loadingPreview, setLoadingPreview] = useState(false);
  const { insertBundle } = bundleHandlers;
  const {
    id: questionnaireId,
    subject,
    headRow,
    endRow,
    questionBundles,
    label,
    eventId,
    language,
  } = questionnaire ?? { language: 'german' };
  const emptyBundles = questionBundles?.length === 0;
  const onLanguageChange = questionnaire
    ? (newLanguage: NonNullable<typeof language>) => patchQuestionnaire({ language: newLanguage })
    : undefined;

  const showPreview = async () => {
    if (questionnaireId) {
      setLoadingPreview(true);
      const allResolved = await PlaceholderS.getUnresolvedPlaceholders(questionnaireId, 'questionnaires');
      setLoadingPreview(false);
      if (allResolved) {
        navigate('preview');
      }
    }
  };

  return (
    <Container p={0} pos="relative">
      {sm && questionnaireId && (
        <QuestionnaireActions
          id={questionnaireId}
          showPreview={showPreview}
          language={language}
          onLanguageChange={onLanguageChange}
        />
      )}
      {!sm && (
        <>
          <Group position="apart" sx={{ alignItems: 'center' }}>
            <Breadcrumbs>
              <Anchor onClick={() => navigate(`/events/${eventId}/documents`)}>Dateien</Anchor>
              <Anchor sx={{ pointerEvents: 'none' }}>
                <b>{loading ? 'Laden ...' : label || 'Nicht gefunden '}</b>
              </Anchor>
            </Breadcrumbs>
            <Group>
              <DocumentLanguagePicker language={language} onLanguageChange={onLanguageChange} />
              {!sm && (
                <Button
                  size="xs"
                  disabled={loadingPreview}
                  leftIcon={loadingPreview ? <Loader size="xs" color="white" /> : <IconEye size={20} />}
                  onClick={showPreview}
                  data-testid={TestS.Navigate.Documents.TO_PREVIEW}
                >
                  Vorschau
                </Button>
              )}
              {sm && (
                <ActionIcon color="blue" variant="filled">
                  <IconEye
                    color="white"
                    size={20}
                    onClick={showPreview}
                    data-testid={TestS.Navigate.Documents.TO_PREVIEW}
                  />
                </ActionIcon>
              )}
              {questionnaireId && (
                <QuestionnaireActions
                  id={questionnaireId}
                  showPreview={showPreview}
                  language={language}
                  onLanguageChange={onLanguageChange}
                />
              )}
            </Group>
          </Group>
          <Divider my="xs" />
        </>
      )}
      <QuestionnaireAlert />
      <Tabs
        styles={{
          tab: {
            '&[data-active]': {
              fontWeight: 'bolder',
            },
          },
        }}
        defaultValue="content"
      >
        <Tabs.List>
          <Tabs.Tab value="content" icon={<IconPilcrow size={14} />}>
            Inhalt
          </Tabs.Tab>
          <Tabs.Tab value="settings" icon={<IconSettings size={14} />}>
            Einstellungen
          </Tabs.Tab>
        </Tabs.List>
        <Tabs.Panel value="content" pt="xl">
          <BookitupTextInput
            mb="sm"
            label="Betreff"
            defaultValue={subject}
            onBlurSave={(value) => patchQuestionnaire({ subject: value })}
          />
          {headRow !== undefined && !isEventScope && (
            <RichInput
              label="Kopfzeile"
              value={headRow}
              patchField={(htmlString) => patchQuestionnaire({ headRow: htmlString })}
              menu={<DocumentBubbleMenu />}
              styles={TiptapS.TextInputStyles}
            />
          )}
          {headRow !== undefined && isEventScope && (
            <EventRichInput
              label="Kopfzeile"
              value={headRow}
              patchField={(htmlString) => patchQuestionnaire({ headRow: htmlString })}
              menu={<DocumentBubbleMenu />}
              styles={TiptapS.TextInputStyles}
            />
          )}
          {loading && <SkeletonItems height={100} />}
          <Box pos="relative">
            <LoadingOverlay visible={insertingBundle} overlayBlur={2} loaderProps={{ size: 'xl' }} />
            {!loading && emptyBundles && (
              <>
                <QuestionBundleSkeleton />
                <LinkText mt={5} mb="xl" ml="sm" onClick={() => insertBundle()}>
                  + Abschnitt hinzufügen
                </LinkText>
              </>
            )}
            {!emptyBundles && (
              <DndProvider backend={HTML5Backend}>
                {questionBundles && (
                  <Stack my="xl">
                    {questionBundles.map((qb, index) => (
                      <QuestionBundle
                        index={index}
                        key={qb.id}
                        bundle={qb}
                        bundleHandlers={bundleHandlers}
                        questionHandlers={questionHandlers}
                        language={language}
                      />
                    ))}
                  </Stack>
                )}
              </DndProvider>
            )}
          </Box>
          {endRow !== undefined && !isEventScope && (
            <RichInput
              label="Fußzeile"
              value={endRow}
              patchField={(htmlString) => patchQuestionnaire({ endRow: htmlString })}
              menu={<DocumentBubbleMenu />}
              styles={{ minHeight: 150 }}
            />
          )}
          {endRow !== undefined && isEventScope && (
            <EventRichInput
              label="Fußzeile"
              value={endRow}
              patchField={(htmlString) => patchQuestionnaire({ endRow: htmlString })}
              menu={<DocumentBubbleMenu />}
              styles={{ minHeight: 150 }}
            />
          )}
          <Space h="xl" />
          <FooterGap />
        </Tabs.Panel>
        <Tabs.Panel
          value="settings"
          pt="xs"
          sx={(theme) => ({ color: theme.colorScheme === 'dark' ? 'white' : 'black' })}
        >
          <QuestionnaireSignatureSection />
          <FooterGap />
        </Tabs.Panel>
      </Tabs>
    </Container>
  );
};

export default QuestionnaireEditor;
